import { AreaFilter, DeviceFilter } from '../types';
import { PrimaryCategory, PRIMARY_CATEGORY_MAP } from '../types';

/**
 * Manages filtering of recommendation cards with primary and secondary categories
 */
export class FilterManager {
  private currentAreaFilter: AreaFilter = 'all';
  private currentDeviceFilter: DeviceFilter = 'all';
  private currentPrimaryFilter: 'all' | PrimaryCategory = 'all';
  private currentSecondaryFilter: 'all' | AreaFilter = 'all';

  /**
   * Initialize filter manager
   * @param deviceFilter - Initial device filter value
   */
  initialize(deviceFilter: DeviceFilter = 'all'): void {
    this.currentDeviceFilter = deviceFilter;
    this.setupPrimaryFilterListeners();
    this.setupSecondaryFilterListeners();
    this.setupAreaFilterListeners(); // Keep for backward compatibility

    // Initialize secondary filter visibility based on default primary filter ('all')
    this.updateSecondaryFilterVisibility(this.currentPrimaryFilter);
  }

  /**
   * Set up primary filter button listeners
   */
  private setupPrimaryFilterListeners(): void {
    const primaryFilterBar = document.getElementById('primary-filter-bar');
    if (!primaryFilterBar) return;

    primaryFilterBar.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('.primary-filter-btn') as HTMLButtonElement;
      if (!btn) return;

      const filter = btn.dataset.primaryFilter as 'all' | PrimaryCategory;

      // Update button states
      document.querySelectorAll('.primary-filter-btn').forEach((button) => {
        button.classList.remove('ring-2', 'ring-offset-2', 'ring-blue-500');
      });
      btn.classList.add('ring-2', 'ring-offset-2', 'ring-blue-500');

      // Update filter
      this.currentPrimaryFilter = filter;

      // Reset secondary filter when primary changes
      this.currentSecondaryFilter = 'all';
      this.resetSecondaryFilterButtons();

      // Show/hide secondary filters based on primary selection
      this.updateSecondaryFilterVisibility(filter);

      this.applyFilters();
    });
  }

  /**
   * Set up secondary filter button listeners
   */
  private setupSecondaryFilterListeners(): void {
    const secondaryFilterBar = document.getElementById('secondary-filter-bar');
    if (!secondaryFilterBar) return;

    secondaryFilterBar.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('.secondary-filter-btn') as HTMLButtonElement;
      if (!btn) return;

      const filter = btn.dataset.secondaryFilter as AreaFilter;

      // Update button states
      document.querySelectorAll('.secondary-filter-btn').forEach((button) => {
        button.classList.remove('ring-1', 'ring-gray-400');
      });
      btn.classList.add('ring-1', 'ring-gray-400');

      // Update filter
      this.currentSecondaryFilter = filter;
      this.applyFilters();
    });
  }

  /**
   * Reset secondary filter buttons to 'all' state
   */
  private resetSecondaryFilterButtons(): void {
    document.querySelectorAll('.secondary-filter-btn').forEach((button) => {
      button.classList.remove('ring-1', 'ring-gray-400');
      if (button.getAttribute('data-secondary-filter') === 'all') {
        button.classList.add('ring-1', 'ring-gray-400');
      }
    });
  }

  /**
   * Show/hide secondary filters based on selected primary category
   * - When "All" is selected: show all secondary filters
   * - When a specific category is selected: show only its secondary filters
   */
  private updateSecondaryFilterVisibility(primaryFilter: 'all' | PrimaryCategory): void {
    const allSecondaryFilters = document.querySelectorAll('.secondary-filter-btn');

    allSecondaryFilters.forEach((button) => {
      const element = button as HTMLElement;
      const primaryCategory = element.dataset.primaryCategory;

      // Show button if:
      // 1. Primary filter is "all" (show all secondary filters)
      // 2. Button's primary category matches the selected primary filter
      if (primaryFilter === 'all' || primaryCategory === primaryFilter) {
        element.style.display = '';
      } else {
        element.style.display = 'none';
      }
    });
  }

  /**
   * Set up area filter button listeners (backward compatibility)
   */
  private setupAreaFilterListeners(): void {
    const filterBar = document.getElementById('filter-bar');
    if (!filterBar) return;

    filterBar.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('.filter-btn') as HTMLButtonElement;
      if (!btn) return;

      const filter = btn.dataset.filter as AreaFilter;

      // Update button states
      document.querySelectorAll('.filter-btn').forEach((button) => {
        button.classList.remove('ring-2', 'ring-offset-2');
      });
      btn.classList.add('ring-2', 'ring-offset-2');

      // Update filter
      this.currentAreaFilter = filter;
      this.applyFilters();
    });
  }

  /**
   * Update device filter
   * @param filter - New device filter value
   */
  setDeviceFilter(filter: DeviceFilter): void {
    this.currentDeviceFilter = filter;
    this.applyFilters();
  }

  /**
   * Apply all filters to cards (supports both new primary/secondary and legacy area filters)
   */
  applyFilters(): void {
    const container = document.getElementById('recommendations-container');
    if (!container) return;

    Array.from(container.children).forEach((card) => {
      const element = card as HTMLElement;

      // Always show the "report unavailable" message
      if (element.dataset.reportUnavailable === 'true') {
        element.style.display = '';
        return;
      }

      const area = element.dataset.area as AreaFilter;
      const deviceType = element.dataset.deviceType;

      // Determine which filtering mode to use
      const usePrimarySecondary = this.currentPrimaryFilter !== 'all' || this.currentSecondaryFilter !== 'all';
      let areaMatch: boolean;

      if (usePrimarySecondary) {
        // New primary/secondary filter logic
        const primaryCategory = PRIMARY_CATEGORY_MAP[area];

        // Check primary filter
        const primaryMatch = this.currentPrimaryFilter === 'all' || primaryCategory === this.currentPrimaryFilter;

        // Check secondary filter
        const secondaryMatch = this.currentSecondaryFilter === 'all' || area === this.currentSecondaryFilter;

        areaMatch = primaryMatch && secondaryMatch;
      } else {
        // Legacy area filter logic (backward compatibility)
        areaMatch = this.currentAreaFilter === 'all' || area === this.currentAreaFilter;
      }

      // Check device filter
      const deviceMatch =
        this.currentDeviceFilter === 'all' ||
        (this.currentDeviceFilter === 'mobile' && deviceType !== 'desktop') ||
        (this.currentDeviceFilter === 'desktop' && deviceType !== 'mobile');

      // Show only if all filters match
      element.style.display = areaMatch && deviceMatch ? '' : 'none';
    });
  }
}
